import React, { useEffect, useState } from 'react';
import { jwtDecode } from "jwt-decode"

import editIcon from '../../../../../../Assets/images/edit-icon.svg';
import closeIcon from '../../../../../../Assets/images/close-icon.svg';
import uploadIcon from "../../../../../../Assets/images/upload-icon.svg";
import humanIcon from "../../../../../../Assets/images/human.png";

import InfoTab from '../AdminProfileItems/InfoTab';
import PasswordTab from '../AdminProfileItems/PasswordTab';
import { useAdminProfile } from '../../../Context/AdminProfileImage/AdminProfileImage';
import "./AdminProfileModal.scss";
import ClipLoader from 'react-spinners/ClipLoader';
import Loader from "../../../../../../Context/Loader/Loader";
import apiClient from '../../../../../../Config/apiClient';
import { showErrorToast, showSuccessToast } from '../../../../../../Context/Toaster/Toaster';
import { ALLOWED_IMAGE_TYPES } from '../../../Utils/Constants';
import { isValidEmail } from "../../../../../../Utils/Validations/validateEmail"
import { isValidCanadianPhone } from "../../../../../../Utils/Validations/validatePhone"
import { isValidPassword } from "../../../../../../Utils/Validations/validatePassword"

const adminPath = process.env.REACT_APP_CC_ADMIN_BASE_PATH;
const signInPath = process.env.REACT_APP_CC_SIGNIN_BASE_PATH

const AdminProfileModal = ({ onClose }) => {
    const [activeTab, setActiveTab] = useState('info');
    const [loading, setLoading] = useState(true);
    const [adminData, setAdminData] = useState(null);
    const [isEditable, setIsEditable] = useState(false);
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [previewImageUrl, setPreviewImageUrl] = useState('');
    const [passwordData, setPasswordData] = useState({ newPassword: '', confirmPassword: '' });
    const [passwordErrors, setPasswordErrors] = useState({});

    const { profileImageUrl, setProfileImageUrl } = useAdminProfile();

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email_id: '',
        contact_no: '',
    });

    useEffect(() => {
        const fetchAdminProfile = async () => {
            try {
                const res = await apiClient.get(`${adminPath}/profile`);
                const data = res.data.data;
                setAdminData(data);
                setFormData({
                    first_name: data.first_name || '',
                    last_name: data.last_name || '',
                    email_id: data.email_id || '',
                    contact_no: data.contact_no || '',
                });
            } catch (err) {
                const msg = err.response ? err.response.data.message : err.message;
                showErrorToast(msg);
            } finally {
                setLoading(false);
            }
        };

        fetchAdminProfile();
    }, []);

    useEffect(() => {
        setPreviewImageUrl(profileImageUrl);
    }, [isEditable, profileImageUrl]);

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const validateFields = () => {
        const newErrors = {};
        if (!formData.first_name) {
            newErrors.first_name = 'First name is required.';
        }

        if (!formData.last_name) {
            newErrors.last_name = 'Last name is required.';
        }

        if (!formData.email_id) {
            newErrors.email_id = 'Email is required.';
        } else if (!isValidEmail(formData.email_id)) {
            newErrors.email_id = 'Invalid email format.';
        }

        if (!formData.contact_no) {
            newErrors.contact_no = 'Contact number is required.';
        } else if (!isValidCanadianPhone(formData.contact_no)) {
            newErrors.contact_no = 'Invalid Canadian phone number.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordFields = () => {
        const newErrors = {};
        const { newPassword, confirmPassword } = passwordData;
        const pwdValidation = isValidPassword(newPassword);

        if (!newPassword) {
            newErrors.newPassword = 'New password is required.';
        } else if (!pwdValidation.valid) {
            newErrors.newPassword = pwdValidation.message;
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm password is required.';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match.';
        }

        setPasswordErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleProfileUpdate = async () => {
        if (!validateFields()) return;

        try {
            setIsLoading(true);
            const payload = new FormData();
            Object.entries(formData).forEach(([key, value]) => payload.append(key, value));

            if (profileImageFile) {
                payload.append('profile_image', profileImageFile);
            } else if (!previewImageUrl) {
                payload.append('remove_image', 'true');
            }

            const res = await apiClient.put(`${adminPath}/update`, payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.status === 'SUCCESS') {
                setAdminData(res.data.data);
                const profileImageUrl = `${res.data.data.profile_image_url}?t=${new Date().getTime()}`;
                setProfileImageUrl(profileImageUrl);
                setPreviewImageUrl(profileImageUrl);
                setIsEditable(false);
                showSuccessToast('Profile updated successfully');
            } else {
                showErrorToast(res.data.message);
            }
        } catch (err) {
            const msg = err.response ? err.response.data.message : err.message;
            console.error(msg);
            showErrorToast(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (!validatePasswordFields()) return;

        try {
            setIsLoading(true);
            const res = await apiClient.put(`${signInPath}/change-password`, passwordData);

            if (res.data.status === 'SUCCESS') {
                showSuccessToast('Password changed successfully');
                setPasswordData({ newPassword: '', confirmPassword: '' });
                setActiveTab('info');
                setIsEditable(false);
            } else {
                showErrorToast(res.data.message);
            }
        } catch (err) {
            const msg = err.response ? err.response.data.message : err.message;
            console.error(msg);
            showErrorToast(msg);
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="cprofile-overlay" id="cprofile-modal">
            <div className="aprofile-content">
                {loading ? (
                    <div style={{ height: '500px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ClipLoader size={45} color="gray" />
                    </div>
                ) : (
                    <>
                        <div className="cpf-top">
                            <h2>Profile</h2>

                            {!isEditable && activeTab === 'info' && (
                                <button className="profile-edit" onClick={() => setIsEditable(true)}>
                                    <img src={editIcon} alt="Edit" />
                                </button>
                            )}
                            <button className="m-close" onClick={onClose}>
                                <img src={closeIcon} alt="close" />
                            </button>
                        </div>

                        <div className={`cprofile-inner edit-profile-inner ${!isEditable ? 'view-mode' : 'edit-mode'}`}>
                            <div className="row">
                                <div className="col-md-5">
                                    <div className="ap-left">
                                        <div className="image-part">
                                            <div className="upload-container" id="drop-area">
                                                <div className="image-preview-wrapper">
                                                    {previewImageUrl ? (
                                                        <>
                                                            <img id="image-preview" src={previewImageUrl} alt="Profile" onError={(e) => { e.target.onerror = null; e.target.src = humanIcon; }} />
                                                            {isEditable && (
                                                                <button className="remove-btn" onClick={() => { setProfileImageFile(null); setPreviewImageUrl(''); const input = document.getElementById('admin-profile-image-input'); if (input) input.value = null; }}>×</button>
                                                            )}
                                                        </>
                                                    ) : !isEditable ? (
                                                        <img className='default-profileimage' src={humanIcon} alt="Default Avatar" />
                                                    ) : (
                                                        <div className="upload-text" onClick={() => document.getElementById('admin-profile-image-input')?.click()} >
                                                            <div className="upload-icon">
                                                                <img className="image-overlay" src={uploadIcon} alt="Upload" />
                                                            </div>
                                                            <h3>Click here, or <span>browse</span></h3>
                                                            <p>Dimension: 300px x 300px</p>
                                                        </div>
                                                    )}
                                                    {isEditable && (
                                                        <input type="file" name="profile_image" id="admin-profile-image-input" accept={ALLOWED_IMAGE_TYPES} style={{ display: 'none' }}
                                                            onChange={(e) => {
                                                                const file = e.target.files[0];
                                                                if (file) {
                                                                    if (file.size > 5 * 1024 * 1024) {
                                                                        setErrors(prev => ({ ...prev, profile_image: 'File size must not exceed 5 MB.' }));
                                                                        e.target.value = null;
                                                                        return;
                                                                    }
                                                                    setErrors(prev => ({ ...prev, profile_image: '' }));
                                                                    setProfileImageFile(file);
                                                                    setPreviewImageUrl(URL.createObjectURL(file));
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            {errors.profile_image && (
                                                <span className="error-text active" style={{ display: 'block', marginTop: '4px' }}>{errors.profile_image}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-7">
                                    <div className="ap-right">
                                        <div className="aptab-header">
                                            <ul className="nav nav-tabs tab-line" role="tablist">
                                                <li className="nav-item">
                                                    <button className={`nav-link tab-btn ${activeTab === 'info' ? 'active' : ''}`} data-bs-toggle="tab" data-bs-target="#info-tab-pane" type="button" role="tab" aria-selected={activeTab === 'info'} onClick={() => setActiveTab('info')}>
                                                        Info
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button className={`nav-link tab-btn ${activeTab === 'password' ? 'active' : ''}`} data-bs-toggle="tab" data-bs-target="#password-tab-pane" type="button" role="tab" aria-selected={activeTab === 'password'} disabled={!isEditable} style={{ opacity: isEditable ? 1 : 0.5, cursor: isEditable ? 'pointer' : 'not-allowed' }} onClick={() => isEditable && setActiveTab('password')}>
                                                        Password Management
                                                    </button>
                                                </li>
                                            </ul>

                                        </div>

                                        <div className="tab-content aptab-body">
                                            <div className={`tab-pane fade ${activeTab === 'info' ? 'show active' : ''}`} id="info-tab-pane" role="tabpanel" aria-labelledby="info-tab" >
                                                <InfoTab
                                                    formData={formData}
                                                    isEditable={isEditable}
                                                    handleChange={handleFieldChange}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className={`tab-pane fade ${activeTab === 'password' ? 'show active' : ''}`} id="password-tab-pane" role="tabpanel" aria-labelledby="password-tab">
                                                <PasswordTab
                                                    isEditable={isEditable}
                                                    passwordData={passwordData}
                                                    handleChange={handlePasswordChange}
                                                    errors={passwordErrors}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isEditable && (
                            <div className="cp-button-group">
                                {activeTab === 'info' ? (
                                    <>
                                        <button className="update-button" onClick={handleProfileUpdate} disabled={isLoading}>
                                            {isLoading ? <Loader /> : 'Update'}
                                        </button>
                                        <button className="cancel-button me-4 float-end" onClick={() => {
                                            setIsEditable(false);
                                            setFormData({
                                                first_name: adminData?.first_name || '',
                                                last_name: adminData?.last_name || '',
                                                email_id: adminData?.email_id || '',
                                                contact_no: adminData?.contact_no || '',
                                            });
                                            setProfileImageFile(null);
                                            setPreviewImageUrl(profileImageUrl);
                                            setErrors({});
                                        }}>
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button className="update-button" onClick={handleChangePassword} disabled={isLoading}>
                                            {isLoading ? <Loader /> : 'Update'}
                                        </button>
                                        <button className="cancel-button me-4 float-end" onClick={() => {
                                            setIsEditable(false);
                                            setActiveTab('info');
                                            setPasswordData({ newPassword: '', confirmPassword: '' });
                                            setPasswordErrors({});
                                        }}>
                                            Cancel
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminProfileModal;
